import React, { FC, Suspense, useEffect, useState } from 'react'
import Modal from '@mui/material/Modal'
import Backdrop from '@mui/material/Backdrop'
import { Shared } from 'ui/shared'
import Fade from '@mui/material/Fade'
import Box from '@mui/material/Box'
import { InputAdornment, TextField, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ITrailerFull } from 'redux/modules/trailers/TReducer'
import { DatePicker } from 'ui/shared/components/DatePicker/DatePicker'
import { SearchUser } from '../SearchUser/SearchUser'
import { TUserSearch } from 'utils/types/TUserSearch'
import { Euro } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { Actions_CurrentTrailer } from 'redux/modules/currentTrailer/Actions'
import { useSelector } from 'react-redux'
import { To } from 'router/To'
import { useNavigate } from 'react-router-dom'

type TFilterTime = (time: Date) => boolean

type Props = {
  isOpen: boolean
  closeModal: () => void
  trailer: ITrailerFull | null
  pickUpDateTime: Date | null
  dropOffDateTime: Date | null
}

export const CreateBookingModal: FC<Props> = (props) => {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    boxShadow: 24,
    borderRadius: '10px',
    maxHeight: 'calc(100vh - 48px)',
    display: 'flex',
  }
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { classes } = useStyles()

  const [pickUpDateTime, setPickUpDateTime] = useState<Date | null>(props.pickUpDateTime)
  const [dropOffDateTime, setDropOffDateTime] = useState<Date | null>(props.dropOffDateTime)
  const [user, setUser] = useState<TUserSearch | null>(null)
  const [price, setPrice] = useState<string>('')

  useEffect(() => {
    setPickUpDateTime(props.pickUpDateTime)
    setDropOffDateTime(props.dropOffDateTime)
  }, [props.pickUpDateTime, props.dropOffDateTime, props.isOpen])

  // const filterPassedTime: TFilterTime = (time) => {
  //   const currentDate = new Date()
  //   const selectedDate = new Date(time)
  //   return currentDate.getTime() < selectedDate.getTime()
  // }

  const onClickCreate = async () => {
    if (!pickUpDateTime || !dropOffDateTime || !props.trailer || !user || !price) {
      return
    }
    try {
      dispatch(
        Actions_CurrentTrailer.saga.createSpecialRent({
          startDate: pickUpDateTime,
          endDate: dropOffDateTime,
          trailerId: props.trailer.id,
          userId: user.entityId,
          price: Number(price),
          onSuccess: () => navigate(To.bookings),
        }),
      )
    } catch {}
  }

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrice(e.target.value)
  }

  const isFormValid = () => {
    return pickUpDateTime !== null && dropOffDateTime !== null && user !== null && price !== ''
  }

  return (
    <Modal
      key={props.isOpen ? 'modal-open' : 'modal-closed'}
      className="modal-portal"
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={props.closeModal}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={props.isOpen}>
        <Box sx={style}>
          <Suspense fallback={<Shared.Preloader color={'secondary'} />}></Suspense>
          <Shared.Card style={{ width: 375 }}>
            <div>
              <Typography variant="h6">Book trailer</Typography>
            </div>
            <div className={classes.col}>
              <div className={classes.trailerInfo}>
                <Typography variant="body1" sx={{ color: '#030712' }}>
                  {props.trailer?.trailerType.name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280' }}>
                  {props.trailer?.plate}
                </Typography>
              </div>

              <DatePicker
                value={pickUpDateTime}
                onChange={setPickUpDateTime}
                onClear={() => setPickUpDateTime(null)}
                placeholderText="Pick-up date and time"
                //filterTime={filterPassedTime}
                label="Pick-up date and time"
                containerStyle={{ marginTop: '1rem' }}
              />
              <DatePicker
                value={dropOffDateTime}
                onChange={setDropOffDateTime}
                onClear={() => setDropOffDateTime(null)}
                placeholderText="Drop-off date and time"
                label="Drop-off date and time"
                containerStyle={{ marginTop: '1rem' }}
              />
              <SearchUser user={user} onChangeUser={setUser} />

              <Typography variant="body2" sx={{ color: '#6B7280', marginTop: '1rem' }}>
                Booking price
              </Typography>
              <TextField
                type="number"
                value={price}
                onChange={handlePriceChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Euro size="1.25rem" />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0, step: 0.01 },
                }}
                variant="standard"
                fullWidth
                margin="normal"
                sx={{ mt: 0 }}
              />
              <Shared.Button
                title={'Book'}
                disabled={!isFormValid()}
                onClick={onClickCreate}
                style={{ marginTop: '1rem' }}
              />
            </div>
          </Shared.Card>
        </Box>
      </Fade>
    </Modal>
  )
}

const useStyles = makeStyles()((theme) => ({
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  col: {
    display: 'flex',
    flexDirection: 'column',
  },
  trailerInfo: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    border: '1px solid #E5E7EB',
    borderRadius: 4,
    padding: '0.75rem 1rem',
  },
}))
